import React, { useEffect } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

type MessageType = "success" | "error" | "warning" | "info";

interface MessageBoxProps {
  open: boolean;
  message: string;
  type?: MessageType;
  autoHideDuration?: number; // ms
  onClose: () => void;
}

const typeStyles: Record<MessageType, any> = {
  success: {
    bg: "#E8F5E9",
    color: "#2E7D32",
    border: "#2E7D32",
  },
  error: {
    bg: "#FDECEA",
    color: "#C62828",
    border: "#C62828",
  },
  warning: {
    bg: "#FFF8E1",
    color: "#ED6C02",
    border: "#ED6C02",
  },
  info: {
    bg: "#E3F2FD",
    color: "#1565C0",
    border: "#1565C0",
  },
};

const MessageBox: React.FC<MessageBoxProps> = ({
  open,
  message,
  type = "info",
  autoHideDuration = 4000,
  onClose,
}) => {
  useEffect(() => {
    if (!open) return;

    const timer = setTimeout(() => {
      onClose();
    }, autoHideDuration);

    return () => clearTimeout(timer);
  }, [open, autoHideDuration, onClose]);

  if (!open) return null;

  const styles = typeStyles[type];

  return (
    <Box
      sx={{
        position: "fixed",
        top: { xs: 16, sm: 24 },
        right: { xs: 16, sm: 24 },
        zIndex: 9999,
        maxWidth: { xs: "90%", sm: 360 },
        backgroundColor: styles.bg,
        border: `1px solid ${styles.border}`,
        borderRadius: "12px",
        px: 2.5,
        py: 2,
        boxShadow: "0px 10px 30px rgba(0,0,0,0.15)",
        display: "flex",
        gap: 1.5,
        alignItems: "flex-start",
      }}
    >
      <Typography
        className="font-helviteca"
        sx={{
          color: styles.color,
          fontSize: { xs: 13, sm: 14 },
          lineHeight: 1.5,
          flex: 1,
        }}
      >
        {message}
      </Typography>

      <IconButton
        size="small"
        onClick={onClose}
        sx={{ color: styles.color, mt: "-2px" }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

export default MessageBox;
